import usePaginatedProductList from "@/admin/community/product-list/hooks/usePaginatedProductList"
import { DeletePathwayButtonMutation } from "@/admin/pathways/__generated__/DeletePathwayButtonMutation.graphql"
import { DeletePathwayButton_productFragment$key } from "@/admin/pathways/__generated__/DeletePathwayButton_productFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabel } from "@/core/context/LabelsContext"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import ProductUtils from "@/product/util/productUtils"
import { displayErrorToast, displaySuccessToast } from "@components/toast/ToastProvider"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import DiscoConfirmationModal from "@disco-ui/modal/DiscoConfirmationModal"
import usePermissions from "@utils/hook/usePermissions"
import { TestIDProps } from "@utils/typeUtils"
import { useState } from "react"
import { useFragment, useMutation } from "react-relay"
import ConnectionHandler from "relay-connection-handler-plus"
import { graphql } from "relay-runtime"

type DeletePathwayButtonProps = {
  children: OverridableDiscoButtonChildren
  pathwayProductKey: DeletePathwayButton_productFragment$key
  onDelete?: VoidFunction
} & TestIDProps

function DeletePathwayButton({
  testid = "DeletePathwayButton",
  pathwayProductKey,
  onDelete,
  children,
}: DeletePathwayButtonProps) {
  const [showModal, setShowModal] = useState(false)
  const activeOrganization = useActiveOrganization()!
  const adminExperienceLabel = useAdminProductLabel("course")
  const adminPathwayLabel = useAdminProductLabel("pathway")
  const adminMembersLabel = useLabel("admin_member")
  const invalidateSidebar = ProductUtils.useInvalidateSidebar()

  const pathwayProduct = useFragment(
    graphql`
      fragment DeletePathwayButton_productFragment on Product {
        id
        name
        type
        ...usePermissionsFragment
      }
    `,
    pathwayProductKey
  )

  const permissions = usePermissions(pathwayProduct)

  const [deletePathway, isLoading] = useMutation<DeletePathwayButtonMutation>(graphql`
    mutation DeletePathwayButtonMutation($id: ID!) {
      response: deletePathway(pathwayProductId: $id) {
        node {
          id @deleteRecord
          product {
            id @deleteRecord
          }
        }
        errors {
          field
          message
        }
      }
    }
  `)

  if (!permissions.has("pathway.delete")) return null

  return (
    <>
      <OverridableDiscoButton onClick={openModal}>{children}</OverridableDiscoButton>
      <DiscoConfirmationModal
        testid={`${testid}.ConfirmationModal`}
        isOpen={showModal}
        onClose={closeModal}
        title={`Delete ${adminPathwayLabel.singular}`}
        description={[
          `This action is permanent and will result in the deletion of the ${adminPathwayLabel.singular}. All related reporting data and settings will be lost.`,
          `${adminExperienceLabel.plural} within this ${adminPathwayLabel.singular} will be unaffected and ${adminMembersLabel.plural} will retain access to ${adminExperienceLabel.plural} they are registered for.`,
        ]}
        modalContentLabel={`Delete ${adminPathwayLabel.singular} Confirmation Modal`}
        typeToConfirmText={pathwayProduct.name}
        confirmButtonProps={{
          onClick: handleDelete,
          disabled: isLoading,
          shouldDisplaySpinner: isLoading,
          children: `I understand, delete ${adminPathwayLabel.singular}`,
        }}
      />
    </>
  )

  function openModal() {
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
  }

  function handleDelete() {
    deletePathway({
      variables: { id: pathwayProduct.id },
      onCompleted({ response }) {
        if (response?.errors?.length) {
          return displayErrorToast(response.errors[0].message)
        }
        usePaginatedProductList.invalidatePathwaysConnections(activeOrganization.id)
        displaySuccessToast({
          message: `${adminPathwayLabel.singular} has been deleted`,
          testid: "DeletePathwayButton.success-toast",
        })
        onDelete?.()
        closeModal()
      },
      onError(err) {
        console.error(err)
        displayErrorToast("An unknown error occurred. Please try again.")
      },
      updater(store, { response }) {
        if (response?.errors?.length) return
        const orgRecord = store.get(activeOrganization.id)
        if (!orgRecord) return
        ConnectionHandler.getConnections(
          orgRecord,
          "AdminPathwaysListTable_pathwayProducts"
        ).forEach((conn) => conn.invalidateRecord())
        const pathwayRecord = orgRecord.getLinkedRecord("products", {
          type: "pathway",
          statuses: ["published"],
        })
        if (pathwayRecord) {
          pathwayRecord.invalidateRecord()
        }
        invalidateSidebar(store)
      },
    })
  }
}

export default DeletePathwayButton
